import { API_BASE_URL, apiClient } from '@/api/client';
import type {
  ReplacementItem,
  SuggestionError,
  SuggestionItem,
  SuggestionJob,
  SuggestionMode,
} from './types';

export const requestSuggestion = async (params: {
  file_id?: string;
  text?: string;
  hint?: 'storyboard';
  mode?: SuggestionMode;
}): Promise<SuggestionJob> => {
  const { data } = await apiClient.post<SuggestionJob>('/v1/suggestions', params);
  return data;
};

export const subscribeSuggestionStream = (
  job: SuggestionJob,
  handlers: {
    onComplete: (items: SuggestionItem[]) => void;
    onError: (error: SuggestionError) => void;
  }
): (() => void) => {
  const source = new EventSource(
    `${API_BASE_URL}/v1/suggestions/${job.job_id}/stream?token=${job.stream_token}`
  );

  source.addEventListener('complete', (event) => {
    const payload = JSON.parse(event.data) as { items: SuggestionItem[] };
    source.close();
    handlers.onComplete(payload.items);
  });

  // 서버 발신 event: error(MessageEvent, data 있음)와 연결 에러(plain Event, data 없음)가
  // 같은 이벤트 이름으로 도착하므로 data 유무로 구분
  source.addEventListener('error', (event) => {
    if (event instanceof MessageEvent) {
      source.close();
      handlers.onError(JSON.parse(event.data) as SuggestionError);
      return;
    }
    // CLOSED는 영구 실패(비 200 응답 포함), 그 외는 EventSource 자동 재연결에 맡김
    if (source.readyState === EventSource.CLOSED) {
      handlers.onError({ message: 'stream connection failed', retryable: true });
    }
  });

  return () => source.close();
};

export const requestReplacement = async (params: {
  audio_file_id: string;
  text?: string;
}): Promise<SuggestionJob> => {
  const { data } = await apiClient.post<SuggestionJob>('/v1/replacements', params);
  return data;
};

export const subscribeReplacementStream = (
  job: SuggestionJob,
  handlers: {
    onComplete: (items: ReplacementItem[]) => void;
    onError: (error: SuggestionError) => void;
  }
): (() => void) => {
  const source = new EventSource(
    `${API_BASE_URL}/v1/replacements/${job.job_id}/stream?token=${job.stream_token}`
  );

  source.addEventListener('complete', (event) => {
    const payload = JSON.parse(event.data) as { duration: number; items: ReplacementItem[] };
    source.close();
    handlers.onComplete(payload.items);
  });

  source.addEventListener('error', (event) => {
    if (event instanceof MessageEvent) {
      source.close();
      handlers.onError(JSON.parse(event.data) as SuggestionError);
      return;
    }
    if (source.readyState === EventSource.CLOSED) {
      handlers.onError({ message: 'stream connection failed', retryable: true });
    }
  });

  return () => source.close();
};
